import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';

const SocketContext = createContext();

export const useSocket = () => {
    return useContext(SocketContext);
};

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const socketRef = React.useRef(null);

    useEffect(() => {
        const connect = () => {
            const userInfo = JSON.parse(localStorage.getItem('userInfo'));
            const token = userInfo?.token;

            if (!token) {
                if (socketRef.current) {
                    socketRef.current.disconnect();
                    socketRef.current = null;
                    setSocket(null);
                }
                return;
            };

            if (socketRef.current) return; // Already connecting or connected

            const backendUrl = process.env.REACT_APP_SOCKET_URL || 'http://localhost:5000';
            
            const newSocket = io(backendUrl, {
                auth: { token },
                transports: ['websocket', 'polling'],
                reconnectionAttempts: 5,
                reconnectionDelay: 5000,
            });

            newSocket.on('connect', () => {
                console.log('Socket Connected:', newSocket.id);
                setIsConnected(true);
            });

            newSocket.on('disconnect', (reason) => {
                console.log('Socket Disconnected:', reason);
                setIsConnected(false);
                if (reason === 'io server disconnect') {
                    newSocket.connect();
                }
            });

            newSocket.on('connect_error', (error) => {
                console.error('Socket Connection Error:', error.message);
            });

            socketRef.current = newSocket;
            setSocket(newSocket);
        };

        connect();

        window.addEventListener('authchange', connect);

        return () => {
            window.removeEventListener('authchange', connect);
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, []);

    // Helper to listen to events with automatic cleanup
    const useSocketEvent = (event, callback) => {
        useEffect(() => {
            if (!socket) return;
            const s = socket;
            s.on(event, callback);
            return () => s.off(event, callback);
        }, [event, callback]);
    };

    const value = {
        socket,
        isConnected,
        useSocketEvent
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};
